import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { deleteBanner, getBanners } from "../../redux/slices/bannerSlice";
import { useQueryParams } from "../../hooks/useQueryParams";
import { TiPencil } from "react-icons/ti";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaImage } from "react-icons/fa";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import { Pagination } from "../common/pagination/Pagination";
import { ConfirmModal } from "../common/ConfirmModal";
import { PageHeader } from "../common/PageHeader";
import { EmptyState } from "../common/EmptyState";
import { StatusBadge } from "../common/StatusBadge";
import { DataTable } from "../common/DataTable";

type PropTypes = {
  onCreate: () => void;
  onUpdate: (id: string) => void;
};

export const ListBanner = ({ onCreate, onUpdate }: PropTypes) => {
  const dispatch = useAppDispatch();
  const { banners, pagination, loading } = useAppSelector(
    (state) => state.banner,
  );
  const { getParam, updateParams } = useQueryParams();

  const page = Number(getParam("page", 1));
  const limit = Number(getParam("limit", 10));
  const isActive = getParam("isActive", "all");
  const categoryId = getParam("categoryId", "all");

  const handelChangeEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateParams({ [name]: value, page: 1 });
  };

  const parserIsActive =
    isActive === "all" ? undefined : isActive === "true" ? true : false;
  const parserCategoryId = categoryId === "all" ? undefined : categoryId;
  useEffect(() => {
    dispatch(
      getBanners({
        page,
        limit,
        isActive: parserIsActive,
        categoryId: parserCategoryId,
      }),
    );
  }, [dispatch, page, limit, parserIsActive, parserCategoryId]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await dispatch(deleteBanner(confirmDeleteId)).unwrap();
      toast.success("Xóa banner thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý banner"
        subtitle={pagination ? `Tổng ${pagination.total} banner` : undefined}
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer text-sm text-white rounded-lg font-medium"
          >
            + Thêm banner
          </button>
        }
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              name="isActive"
              value={isActive}
              onChange={handelChangeEvent}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="true">Hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>
            <select
              name="categoryId"
              value={categoryId}
              onChange={handelChangeEvent}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tất cả danh mục</option>
              {banners.map(
                (banner) =>
                  banner.categoryId && (
                    <option value={banner.categoryId._id} key={banner._id}>
                      {banner.categoryId.name}
                    </option>
                  ),
              )}
            </select>
          </div>
          <select
            name="limit"
            value={limit}
            onChange={handelChangeEvent}
            className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
          >
            <option value="10">10 / trang</option>
            <option value="20">20 / trang</option>
            <option value="30">30 / trang</option>
            <option value="50">50 / trang</option>
          </select>
        </div>

        <DataTable
          data={banners}
          rowKey={(b) => b._id}
          loading={loading.list}
          minWidth="900px"
          columns={[
            {
              key: "title",
              header: "Tiêu đề",
              align: "left",
              render: (b) => (
                <span className="font-medium text-gray-900">{b.title}</span>
              ),
            },
            {
              key: "image",
              header: "Ảnh",
              align: "center",
              width: "80px",
              render: (b) => (
                <img
                  src={b.image?.url}
                  alt={b.title}
                  className="h-12 w-12 border border-gray-200 rounded-lg object-cover mx-auto"
                />
              ),
            },
            {
              key: "orientation",
              header: "Định hướng",
              align: "center",
              render: (b) => (b.orientation === "horizontal" ? "Ngang" : "Dọc"),
            },
            {
              key: "link",
              header: "Link",
              align: "left",
              render: (b) =>
                b.link ? (
                  <span className="text-blue-600 truncate inline-block max-w-50">
                    {b.link}
                  </span>
                ) : (
                  "—"
                ),
            },
            {
              key: "position",
              header: "Vị trí",
              align: "center",
              render: (b) =>
                b.position === "home_top" ? "Trang chủ" : "Danh mục",
            },
            {
              key: "status",
              header: "Trạng thái",
              align: "center",
              render: (b) =>
                b.isActive ? (
                  <StatusBadge tone="success" label="Hoạt động" />
                ) : (
                  <StatusBadge tone="danger" label="Ngừng" />
                ),
            },
            {
              key: "category",
              header: "Danh mục",
              align: "left",
              render: (b) => b.categoryId?.name ?? "—",
            },
            {
              key: "actions",
              header: "Thao tác",
              align: "right",
              width: "100px",
              render: (b) => (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    title="Sửa"
                    onClick={() => onUpdate(b._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <TiPencil className="text-lg" />
                  </button>
                  <button
                    type="button"
                    title="Xóa"
                    onClick={() => setConfirmDeleteId(b._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <RiDeleteBinLine className="text-lg" />
                  </button>
                </div>
              ),
            },
          ]}
          empty={
            <EmptyState
              icon={<FaImage />}
              title="Chưa có banner nào"
              description="Thêm banner đầu tiên để hiển thị trên trang chủ"
              action={
                <button
                  type="button"
                  onClick={onCreate}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white cursor-pointer"
                >
                  + Thêm banner
                </button>
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa banner này? Hành động này không thể hoàn tác."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
